import clsx from "clsx";
import { useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import FilterButton from "common/components/ui/FilterButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { compareWorkerAccounts } from "common/helpers/utils";
import type { WorkspaceAccount } from "common/types";

import { useCampaignContext } from "../../context/CampaignContext";
import ActionModal from "./ActionModal";
import WorkerAccount from "./WorkerAccount";

import NoAccountsPlaceholderImage from "assets/images/empty-placeholders/disconnected.png";

type ActionType = "add" | "remove" | "reconnect" | "license" | "assignSeat";

export default function WorkerAccounts() {
  const { owners, uses_connection_note: isUsingNote } = useCampaignContext();
  const { accounts } = useSelectedWorkspaceContext();

  const [isOpen, setIsOpen] = useState(false);
  const [actionType, setActionType] = useState<ActionType>(null);
  const [selectedAccount, setSelectedAccount] =
    useState<WorkspaceAccount>(null);

  const isMulti = owners.length > 1;
  const workerAccounts = accounts
    .filter(({ id }) => owners.includes(id))
    .sort(compareWorkerAccounts);
  const hasWorkerAccounts = !!workerAccounts.length;

  let label = "No accounts";
  if (isMulti) {
    label = `${owners.length} accounts`;
  } else if (workerAccounts.length) {
    label = workerAccounts[0].full_name;
  }

  const invalidWorkerAccounts = workerAccounts.filter(
    ({ state, license, premium, type }) => {
      const isMissingPremium = isUsingNote && !premium;
      const isMissingLicense = type === "user" && !license;
      const isLoggedOut = state !== "LoggedIn";

      return isMissingPremium || isMissingLicense || isLoggedOut;
    },
  );

  let borderColor = "";
  if (
    invalidWorkerAccounts.length === workerAccounts.length ||
    !hasWorkerAccounts
  ) {
    borderColor = "border-red-400";
  } else if (invalidWorkerAccounts.length) {
    borderColor = "border-yellow-400";
  }

  function openModal(type: ActionType, account: WorkspaceAccount = null) {
    setActionType(type);

    if (account) {
      setSelectedAccount(account);
    }

    setIsOpen(true);
  }

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <FilterButton
            size="sm"
            className={clsx("max-w-44", borderColor)}
            leftIcon={
              hasWorkerAccounts ? (
                <div className="flex shrink-0 flex-row -space-x-1.5 rtl:space-x-reverse">
                  {workerAccounts
                    .slice(0, 3)
                    .map(({ full_name, id, picture_url: pictureUrl }) => (
                      <ProfileImage
                        key={id}
                        size="2xs"
                        className={clsx(
                          isMulti &&
                            "ring-1 ring-white group-hover:ring-black-100 duration-300 ease-in-out transition-colors",
                        )}
                        src={pictureUrl}
                        alt={full_name}
                      />
                    ))}
                </div>
              ) : (
                <ProfileImage size="2xs" src="" />
              )
            }
          >
            {label}
          </FilterButton>
        </PopoverTrigger>

        <PopoverContent
          className="w-[284px] min-w-[var(--radix-popper-anchor-width)]"
          align="end"
        >
          {hasWorkerAccounts ? (
            <div className="flex max-h-[216px] flex-col gap-y-0.5 overflow-y-auto p-1.5 scrollbar-thin scrollbar-thumb-black-200">
              {workerAccounts.map((account) => (
                <WorkerAccount
                  key={account.id}
                  account={account}
                  openModal={openModal}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-y-1 p-3">
              <img src={NoAccountsPlaceholderImage} alt="" />
              <h3 className="text-center text-sm-bold">No accounts</h3>
              <p className="text-center text-xs-medium text-black-500">
                All the accounts have been removed. Please add at least one
                account for the campaign to start working
              </p>
            </div>
          )}

          <div
            className={clsx(
              "flex gap-x-2 p-1.5",
              hasWorkerAccounts && "border-t border-black-200",
            )}
          >
            <RenderIf condition={hasWorkerAccounts}>
              <Button
                size="sm"
                className="flex-1"
                variant="outline-black"
                onClick={() => openModal("remove")}
                disabled={owners.length === 1} // There needs to be at least 1 worker account
              >
                Remove account
              </Button>
            </RenderIf>

            <Button
              size="sm"
              className="flex-1"
              variant="secondary-black"
              onClick={() => openModal("add")}
              disabled={owners.length === accounts.length} // Disable if all accounts are already selected
            >
              Add account
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <ActionModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        type={actionType}
        selectedAccount={selectedAccount}
      />
    </>
  );
}
